<template>
  <div>
    <Head
      :title="`${$t('actions.update_model_detail', [$t('models.member')])} - ${member.display_name}`"/>
    <div class="flex items-center mb-8">
      <h1 class="text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" :href="route('admin.members.index')">
          {{ $t('models.member') }}
        </Link>
        <span class="text-indigo-400 font-medium mx-2">/</span>
        {{ member.display_name }}
      </h1>
      <img v-if="member.photo" class="block ml-4 w-12 h-12 rounded-full object-contain"
           :src="member.photo"/>
      <div class="ml-auto">
        <Link v-if="!member.has_user" class="inline-block btn-indigo mx-2"
              :href="route('admin.members.create-user', member.uuid)">
          <span>{{
              $t('actions.associate_model', [$t('labels.new_model', [$t('models.user')])])
            }}</span>
        </Link>
        <Link v-if="!member.has_user" class="inline-block btn-indigo mx-2"
              :href="route('admin.members.associate-user', member.uuid)">
          <span>{{
              $t('actions.associate_model', [$t('labels.existing_model', [$t('models.user')])])
            }}</span>
        </Link>
        <Link class="inline-block btn-indigo" :href="route('admin.members.index')">
          <span>{{ $t('ui.back') }}</span>
        </Link>
      </div>
    </div>

    <trashed-message v-if="member.deleted_at" class="mb-6" @restore="restore">
      {{ $t('alerts.current_model_deleted', [$t('models.member')]) }}
    </trashed-message>
    <div class="bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.membership_number" :error="form.errors.membership_number"
                      class="pb-8 pr-6 w-full lg:w-1/6"
                      :label="$t('fields.member.membership_number')" autofocus/>
          <text-input v-model="form.join_date" :error="form.errors.join_date"
                      :placeholder="dateInputPlaceholder"
                      class="pb-8 pr-6 w-full lg:w-2/6" :label="$t('fields.member.join_date')"/>
          <text-input v-model="form.nric" :error="form.errors.nric"
                      class="pb-8 pr-6 w-full lg:w-3/6" :label="$t('fields.member.nric')"/>
          <text-input v-model="form.full_name" :error="form.errors.full_name"
                      class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.full_name')"/>
          <text-input v-model="form.full_name_cn" :error="form.errors.full_name_cn"
                      class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.full_name_cn')"/>
          <text-input v-model="form.mobile" :error="form.errors.mobile"
                      class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.mobile')"/>
          <text-input v-model="form.email" :error="form.errors.email"
                      class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.email')"/>
          <select-input v-model="form.gender" :error="form.errors.gender"
                        class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.gender')">
            <option :value="null"/>
            <option v-for="gender in genders" :value="gender">{{ gender }}</option>
          </select-input>
          <text-input v-model="form.dob" :error="form.errors.dob"
                      :placeholder="dateInputPlaceholder"
                      class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.dob')"/>
          <textarea-input v-model="form.address" :error="form.errors.address" rows="3"
                          class="pb-8 pr-6 w-full" :label="$t('fields.member.address')"/>
          <text-input v-model="form.state" :error="form.errors.state"
                      class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.state')"/>
          <select-input v-model="form.is_active" :error="form.errors.is_active"
                        class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('fields.member.is_active')">
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select-input>
          <textarea-input v-model="form.remarks" :error="form.errors.remarks" rows="3"
                          class="pb-8 pr-6 w-full" :label="$t('fields.member.remarks')"/>
          <file-input v-model="form.photo" :error="form.errors.photo"
                      class="pb-8 pr-6 w-full lg:w-1/2" type="file" accept="image/*"
                      label="Photo"/>
          <div v-if="member.photo" class="pb-8 pr-6 w-full lg:w-1/2">
            <img :src="member.photo" alt="portrait"
                 class="p-1 border rounded max-h-36 object-contain">
          </div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!member.deleted_at" class="text-red-600 hover:underline"
                  tabindex="-1" type="button" @click="destroy">
            {{ $t('actions.delete_model', [$t('models.member')]) }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('actions.save') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Layouts/Authenticated'
import TextInput from '@/Inputs/TextInput'
import FileInput from '@/Inputs/FileInput'
import SelectInput from '@/Inputs/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import TextareaInput from "@/Inputs/TextareaInput";

export default {
  components: {
    TextareaInput,
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    member: Object,
    genders: Array,
    states: Array,
    regions: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        membership_number: this.member.membership_number,
        join_date: this.member.join_date,
        nric: this.member.nric,
        full_name: this.member.full_name,
        full_name_cn: this.member.full_name_cn,
        mobile: this.member.mobile,
        email: this.member.email,
        gender: this.member.gender,
        dob: this.member.dob,
        address: this.member.address,
        state: this.member.state,
        region: this.member.region,
        remarks: this.member.remarks,
        is_active: this.member.is_active,
        photo: null,
      }),
      dateInputPlaceholder: `e.g. ${new Date().toLocaleDateString('en-CA')}`,
    }
  },
  methods: {
    update() {
      this.form.post(route('admin.members.update', this.member.uuid), {
        onSuccess: () => this.form.reset('photo'),
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this member?')) {
        this.$inertia.delete(route('admin.members.destroy', this.member.uuid))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this member?')) {
        this.$inertia.put(route('admin.members.restore', this.member.uuid))
      }
    },
  },
}
</script>
